/**
 * Inject a proxy on the target with the provided decorator.
 * @param {{}} resource
 * @param {{}} decorator
 * @return {{name: *}|*}
 */
function decorateResource(resource, decorator) {
    return new Proxy({name: resource.name}, {
        get(target, operationName) {
            if (!target[operationName]) {
                target[operationName] = new Proxy(resource[operationName], decorator);
            }

            return target[operationName];
        },
    });
}

/**
 * Assign the provided filter to the target resource.
 * @param {{}} resource
 * @param {PropertyKey} filterName
 * @param {{}} filter
 * @return {{}}
 */
function extendResource(resource, [filterName, filter]) {
    return Object.assign(resource, {
        [filterName]: decorateResource(resource, filter),
    });
}

/**
 * Proxy the execution through multiple instances and execute the matching
 * method.
 * @param {PropertyKey} name
 * @param {[]} resources
 * @throws Will throw an error if there is a name collision on the
 * method name within two or more instances
 * @return {{name: *}|undefined|*}
 */
function combineResources(name, resources) {
    return new Proxy({name}, {
        get(target, property) {
            if (!target[property]) {
                const operations = resources.reduce(
                    (memo, resource) => (
                        resource[property]
                            ? memo.concat(resource[property])
                            : memo
                    ),
                    [],
                );

                // Catch the edge case when more clients have the same method.
                if (operations.length > 1) {
                    throw new Error('Found API conflict');
                }

                if (operations.length === 0) {
                    return undefined;
                }

                target[property] = operations.pop();
            }

            return target[property];
        },
    });
}

/**
 * Proxy API clients into a single entity and inject additional methods and features
 * into the new instance.
 * @param {RebillyAPI[]} clients
 * @param {{}} decorators
 * @param {{}} filters
 * @return {{}|undefined|*}
 */
function combineClients(clients, decorators, filters) {
    return new Proxy({}, {
        get(compositeClient, resourceName) {
            if (!compositeClient[resourceName]) {
                const resources = clients.reduce(
                    (memo, client) => (
                        client[resourceName]
                            ? memo.concat(client[resourceName])
                            : memo
                    ),
                    [],
                );

                if (resources.length === 0) {
                    return undefined;
                }

                let resource = combineResources(resourceName, resources);
                resource = Object.values(decorators).reduce(decorateResource, resource);
                resource = Object.entries(filters).reduce(extendResource, resource);

                compositeClient[resourceName] = resource;
            }

            return compositeClient[resourceName];
        },
    });
}

export default class ApiHandlerDecorator {
    /**
     * Create an API handler from multiple clients while injecting
     * additional features into the end result.
     * @param {RebillyAPI[]} clients
     * @param {{}} decorators
     * @param {{}} filters
     * @return {{}|*}
     */
    constructor(clients, decorators, filters) {
        return combineClients(clients, decorators, filters);
    }
}
